import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaRobot,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUsers,
  FaSync,
  FaShare,
  FaDownload,
  FaEdit,
  FaSave,
  FaTimes,
  FaBus,
  FaUtensils,
  FaLandmark,
  FaArrowLeft,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import tripPlannerAPI from '../services/tripPlannerAPI';
import './TripPlanPage.css';

const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

const buildDownloadText = (plan) => {
  const lines = [
    plan.title,
    `Destination: ${plan.destination}`,
    `Dates: ${new Date(plan.checkIn).toLocaleDateString()} – ${new Date(plan.checkOut).toLocaleDateString()}`,
    '',
    plan.itinerary?.summary || '',
    '',
    '--- DAILY ITINERARY ---',
  ];

  (plan.itinerary?.days || []).forEach((day) => {
    lines.push('');
    lines.push(`Day ${day.day}: ${day.title} (${day.date})`);
    (day.activities || []).forEach((act) => {
      lines.push(`  ${act.time} – ${act.title}`);
      if (act.description) lines.push(`    ${act.description}`);
      if (act.estimatedCost) lines.push(`    Cost: ${formatCurrency(act.estimatedCost)}`);
    });
  });

  const budget = plan.itinerary?.budget || {};
  lines.push('');
  lines.push('--- ESTIMATED BUDGET ---');
  lines.push(`Stay: ${formatCurrency(budget.stay)}`);
  lines.push(`Food: ${formatCurrency(budget.food)}`);
  lines.push(`Transport: ${formatCurrency(budget.transport)}`);
  lines.push(`Activities: ${formatCurrency(budget.activities)}`);
  lines.push(`Total: ${formatCurrency(budget.total)}`);
  lines.push('');
  lines.push('Generated by UBSA AI Trip Planner');

  return lines.join('\n');
};

const TripPlanPage = () => {
  const navigate = useNavigate();
  const { planId, shareToken } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDays, setEditDays] = useState([]);
  const isSharedView = Boolean(shareToken);

  useEffect(() => {
    if (shareToken) {
      loadSharedPlan(shareToken);
      return;
    }

    if (planId) {
      loadPlan(planId);
      return;
    }

    setLoading(false);
  }, [planId, shareToken]);

  const loadPlan = async (id) => {
    setLoading(true);
    try {
      const res = await tripPlannerAPI.getPlan(id);
      setPlan(res.data?.plan);
    } catch {
      toast.error('Trip plan not found');
      navigate('/trips');
    } finally {
      setLoading(false);
    }
  };

  const loadSharedPlan = async (token) => {
    setLoading(true);
    try {
      const res = await tripPlannerAPI.getSharedPlan(token);
      setPlan(res.data?.plan);
    } catch {
      toast.error('Shared trip plan not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!plan?._id || isSharedView) return;
    setRegenerating(true);
    try {
      const res = await tripPlannerAPI.regenerate(plan._id);
      setPlan(res.data?.plan);
      toast.success('Trip plan regenerated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  const handleShare = async () => {
    if (!plan?._id || isSharedView) return;
    setSharing(true);
    try {
      const res = await tripPlannerAPI.share(plan._id);
      const url = res.data?.shareUrl;
      if (url && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied to clipboard!');
      } else if (url) {
        toast.success(`Share link: ${url}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const handleDownload = () => {
    if (!plan) return;
    const text = buildDownloadText(plan);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ubsa-trip-plan-${plan.destination || 'trip'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Trip plan downloaded!');
  };

  const startEdit = () => {
    setEditTitle(plan.title);
    setEditDays(JSON.parse(JSON.stringify(plan.itinerary?.days || [])));
    setEditMode(true);
  };

  const saveEdit = async () => {
    try {
      const res = await tripPlannerAPI.updatePlan(plan._id, {
        title: editTitle,
        itinerary: { days: editDays },
      });
      setPlan(res.data?.plan);
      setEditMode(false);
      toast.success('Changes saved!');
    } catch {
      toast.error('Failed to save changes');
    }
  };

  const updateActivity = (dayIdx, actIdx, field, value) => {
    setEditDays((prev) => {
      const next = [...prev];
      next[dayIdx] = { ...next[dayIdx] };
      next[dayIdx].activities = [...next[dayIdx].activities];
      next[dayIdx].activities[actIdx] = {
        ...next[dayIdx].activities[actIdx],
        [field]: value,
      };
      return next;
    });
  };

  if (loading) {
    return (
      <div className="trip-plan-page">
        <Navbar />
        <div className="trip-plan-container">
          <div className="trip-plan-loading">
            <div className="spinner" />
            <h2>Loading trip plan...</h2>
            <p>Creating a personalized day-wise itinerary with attractions, dining & transport tips.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="trip-plan-page">
        <Navbar />
        <div className="trip-plan-container">
          <p>No trip plan found.</p>
          <button className="trip-plan-btn trip-plan-btn-primary" onClick={() => navigate('/trips')}>
            Go to My Trips
          </button>
        </div>
      </div>
    );
  }

  const budget = plan.itinerary?.budget || {};
  const days = editMode ? editDays : plan.itinerary?.days || [];

  return (
    <div className="trip-plan-page">
      <Navbar />
      <div className="trip-plan-container">
        {isSharedView && (
          <div className="trip-shared-banner">
            This is a shared trip plan from UBSA AI Trip Planner.
          </div>
        )}

        <div className="trip-plan-hero">
          <div className="trip-plan-hero-badge">
            <FaRobot /> UBSA AI Trip Planner
          </div>
          {editMode ? (
            <input
              className="trip-edit-input"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              style={{ color: '#1a1a1a', marginBottom: 8 }}
            />
          ) : (
            <h1>{plan.title}</h1>
          )}
          <p>{plan.itinerary?.summary}</p>
          <div className="trip-plan-meta">
            <span className="trip-plan-meta-item">
              <FaMapMarkerAlt /> {plan.destination}
            </span>
            <span className="trip-plan-meta-item">
              <FaCalendarAlt />
              {new Date(plan.checkIn).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
              {' – '}
              {new Date(plan.checkOut).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="trip-plan-meta-item">
              <FaUsers />
              {plan.guests?.adults || 1} adults
              {plan.guests?.children > 0 && `, ${plan.guests.children} children`}
            </span>
          </div>
        </div>

        {!isSharedView && (
          <div className="trip-plan-actions">
            <button
              className="trip-plan-btn trip-plan-btn-ghost"
              onClick={() => navigate('/trips')}
            >
              <FaArrowLeft /> My Trips
            </button>
            {editMode ? (
              <>
                <button className="trip-plan-btn trip-plan-btn-primary" onClick={saveEdit}>
                  <FaSave /> Save Changes
                </button>
                <button className="trip-plan-btn trip-plan-btn-ghost" onClick={() => setEditMode(false)}>
                  <FaTimes /> Cancel
                </button>
              </>
            ) : (
              <>
                <button className="trip-plan-btn trip-plan-btn-secondary" onClick={startEdit}>
                  <FaEdit /> Edit Plan
                </button>
                <button
                  className="trip-plan-btn trip-plan-btn-secondary"
                  onClick={handleRegenerate}
                  disabled={regenerating}
                >
                  <FaSync className={regenerating ? 'fa-spin' : ''} />
                  {regenerating ? 'Regenerating...' : 'Regenerate'}
                </button>
                <button
                  className="trip-plan-btn trip-plan-btn-secondary"
                  onClick={handleShare}
                  disabled={sharing}
                >
                  <FaShare /> Share
                </button>
                <button className="trip-plan-btn trip-plan-btn-primary" onClick={handleDownload}>
                  <FaDownload /> Download
                </button>
              </>
            )}
          </div>
        )}

        {isSharedView && (
          <div className="trip-plan-actions">
            <button className="trip-plan-btn trip-plan-btn-primary" onClick={handleDownload}>
              <FaDownload /> Download Plan
            </button>
          </div>
        )}

        <div className="trip-budget-card">
          <h2>Estimated Trip Budget</h2>
          <div className="trip-budget-grid">
            <div className="trip-budget-item">
              <div className="label">Stay</div>
              <div className="amount">{formatCurrency(budget.stay)}</div>
            </div>
            <div className="trip-budget-item">
              <div className="label">Food</div>
              <div className="amount">{formatCurrency(budget.food)}</div>
            </div>
            <div className="trip-budget-item">
              <div className="label">Transport</div>
              <div className="amount">{formatCurrency(budget.transport)}</div>
            </div>
            <div className="trip-budget-item">
              <div className="label">Activities</div>
              <div className="amount">{formatCurrency(budget.activities)}</div>
            </div>
          </div>
          <div className="trip-budget-total">
            <span>Total Estimated Budget</span>
            <span>{formatCurrency(budget.total)}</span>
          </div>
        </div>

        <h2 style={{ marginBottom: 16, fontSize: 20 }}>Day-wise Itinerary</h2>

        {days.map((day, dayIdx) => (
          <div key={day.day || dayIdx} className="trip-day-card">
            <div className="trip-day-header">
              <h3>
                Day {day.day}: {day.title}
              </h3>
              <span className="trip-day-date">{day.date}</span>
            </div>
            {(day.activities || []).map((act, actIdx) => (
              <div key={actIdx} className="trip-activity">
                {editMode ? (
                  <div className="trip-activity-body" style={{ width: '100%' }}>
                    <input
                      className="trip-edit-input"
                      value={act.time}
                      onChange={(e) => updateActivity(dayIdx, actIdx, 'time', e.target.value)}
                      placeholder="Time"
                    />
                    <input
                      className="trip-edit-input"
                      value={act.title}
                      onChange={(e) => updateActivity(dayIdx, actIdx, 'title', e.target.value)}
                      placeholder="Activity title"
                    />
                    <textarea
                      className="trip-edit-textarea"
                      value={act.description}
                      onChange={(e) => updateActivity(dayIdx, actIdx, 'description', e.target.value)}
                      placeholder="Description"
                    />
                  </div>
                ) : (
                  <>
                    <div className="trip-activity-time">{act.time}</div>
                    <div className="trip-activity-body">
                      <div className="trip-activity-title">{act.title}</div>
                      {act.description && (
                        <div className="trip-activity-desc">{act.description}</div>
                      )}
                      <div className="trip-activity-meta">
                        <span className={`trip-activity-type ${act.type || 'attraction'}`}>
                          {act.type || 'activity'}
                        </span>
                        {act.duration && <span>{act.duration}</span>}
                        {act.estimatedCost > 0 && <span>{formatCurrency(act.estimatedCost)}</span>}
                        {act.location && <span>{act.location}</span>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ))}

        {(plan.itinerary?.transport || []).length > 0 && (
          <div className="trip-section">
            <h2>
              <FaBus style={{ marginRight: 8 }} />
              Transport Options
            </h2>
            {plan.itinerary.transport.map((t, i) => (
              <div key={i} className="trip-transport-item">
                <strong>{t.type}</strong>: {t.from} → {t.to}
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  {t.travelTime && <span>{t.travelTime} · </span>}
                  {t.estimatedCost > 0 && <span>{formatCurrency(t.estimatedCost)} · </span>}
                  {t.notes}
                </div>
              </div>
            ))}
          </div>
        )}

        {plan.itinerary?.recommendations && (
          <>
            {plan.itinerary.recommendations.attractions?.length > 0 && (
              <div className="trip-section">
                <h2>
                  <FaLandmark style={{ marginRight: 8 }} />
                  Nearby Attractions
                </h2>
                <div className="trip-rec-grid">
                  {plan.itinerary.recommendations.attractions.map((r, i) => (
                    <div key={i} className="trip-rec-card">
                      <h4>{r.name}</h4>
                      <p>{r.description}</p>
                      {r.distance && <p style={{ marginTop: 4 }}>{r.distance}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {plan.itinerary.recommendations.restaurants?.length > 0 && (
              <div className="trip-section">
                <h2>
                  <FaUtensils style={{ marginRight: 8 }} />
                  Recommended Restaurants
                </h2>
                <div className="trip-rec-grid">
                  {plan.itinerary.recommendations.restaurants.map((r, i) => (
                    <div key={i} className="trip-rec-card">
                      <h4>{r.name}</h4>
                      <p>{r.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TripPlanPage;
